import { CrudFilters, DataProvider } from '@refinedev/core';
import { stringify } from '@refinedev/simple-rest';
import { AxiosInstance } from 'axios';

import { getSelectedCountryId } from './contexts/CountryContext';

export const dataProvider = (apiUrl: string, axiosInstance: AxiosInstance) => {
  const escapeRegexSpecialChars = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sanitizeQueryParam = (key: string, value: any) => {
    // Prevent unescaped regex metacharacters from breaking backend searches
    if (key === 'search' && typeof value === 'string') {
      return escapeRegexSpecialChars(value);
    }

    return value;
  };

  const generateFilters = (filters: CrudFilters = []) => {
    const queryFilters: Record<string, any> = {};

    filters?.forEach(filter => {
      if ('field' in filter && filter.operator && filter.value !== undefined) {
        const { field, value } = filter;
        const sanitizedValue = sanitizeQueryParam(`${field}`, value);

        queryFilters[`${field}`] = sanitizedValue;
      }
    });

    /**
     * Parametro generalizado para funcionalidades de app operaciones
     */
    queryFilters.only_staff = true;

    const selectedCountryId = getSelectedCountryId();

    if (selectedCountryId != undefined) {
      queryFilters.only_br = selectedCountryId;
    }

    return queryFilters;
  };

  const provider: DataProvider = {
    create: async ({ resource, variables }) => {
      const url = `${apiUrl}/${resource}`;
      const { data } = await axiosInstance.post(url, variables);

      return {
        data,
      };
    },
    deleteOne: async ({ resource, id, variables }) => {
      const url = `${apiUrl}/${resource}/${id}`;
      const { data } = await axiosInstance.delete(url, {
        data: variables,
      });

      return {
        data,
      };
    },
    getList: async params => {
      const { resource, pagination, sorters, filters, meta } = params;
      const url = `${apiUrl}/${resource}`;
      const { currentPage = 1, pageSize = 20 } = pagination ?? {};
      const query: Record<string, any> = {
        page: currentPage,
        page_size: pageSize,
      };

      if (sorters && sorters.length > 0) {
        const sortersString = sorters.map(sorter => {
          return `${sorter.order === 'asc' ? '' : '-'}${sorter.field}`;
        });

        query.order_by = sortersString.join(',');
      }

      const queryFilters = generateFilters(filters);

      if (meta) {
        Object.entries(meta).forEach(([key, value]) => {
          if (
            value !== undefined &&
            value !== null &&
            key != 'queryContext' &&
            key != 'queryKey' &&
            key != 'signal'
          ) {
            query[key] = sanitizeQueryParam(key, value);
          }
        });
      }

      const { data } = await axiosInstance.get(
        `${url}?${stringify({ ...query, ...queryFilters })}`,
      );

      if (Array.isArray(data)) {
        return {
          data: data,
          total: data.length,
        };
      } else {
        const { count, results } = data ?? {};

        return {
          data: results,
          total: count,
        };
      }
    },
    getOne: async params => {
      const { resource, meta, id } = params;
      const url = `${apiUrl}/${resource}/${id}`;
      const query: Record<string, any> = {};

      if (meta) {
        Object.entries(meta).forEach(([key, value]) => {
          if (
            value !== undefined &&
            value !== null &&
            key != 'queryContext' &&
            key != 'queryKey' &&
            key != 'signal'
          ) {
            query[key] = sanitizeQueryParam(key, value);
          }
        });
      }

      query.only_staff = true;

      const selectedCountryId = getSelectedCountryId();

      if (selectedCountryId != undefined) {
        query.only_br = selectedCountryId;
      }

      const { data } = await axiosInstance.get(`${url}?${stringify({ ...query })}`);

      return {
        data,
      };
    },
    update: async ({ resource, id, variables }) => {
      const url = `${apiUrl}/${resource}/${id}`;
      const updatedData = Object.fromEntries(
        Object.entries(variables as Record<string, unknown>).map(([key, value]) => [
          key,
          value === undefined ? null : value,
        ]),
      );
      const { data } = await axiosInstance.patch(url, updatedData);

      return {
        data,
      };
    },
    getApiUrl: () => apiUrl,
  };

  return provider;
};
